/* eslint-disable react/jsx-handler-names */
import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { parseQuery } from 'url-lib';
import isEqual from 'lodash/isEqual';

import FilterButton from './FilterButton';

import './FilterSection.scss';

// if we're not in an "expand" state, we only show the first x filters (x = moreThreshold)
export const getRadioValues = (filters) => {
    const entries = Object.entries(filters);

    return {
        radioValues: entries.map(([key, label]) => ({
            display: label,
            value: key,
        })),
    };
};

// eslint-disable-next-line react/no-multi-comp
class FilterSection extends React.Component {
    static propTypes = {
        /**
         * The filter options to be presented to a user
         */
        filters: PropTypes.object.isRequired,
        /**
         * Callback to trigger when users pick a different filter
         */
        onOptionChange: PropTypes.func.isRequired,
        /**
         * The window location provided by withRouter
         */
        location: PropTypes.object.isRequired,
        /**
         * Relation between url parameters and the section's options
         */
        urlParameterMapping: PropTypes.object,
        /**
         * Boolean if the Filter Section is a mobile experience
         */
        isMobile: PropTypes.bool,
        /**
         * Leverage app internal state for filter
         * value instead of the URL
         */
        useAppState: PropTypes.bool,
        /**
         * The current active filter according
         * to application state
         */
        activeFilterOption: PropTypes.string,
    };

    static defaultProps = {
        renderFilters: (radios, selectedFilter, onClick) =>
            radios.map(({ display, value }) => (
                <div key={value}>
                    <FilterButton
                        isMobile={true}
                        onClick={() => onClick(value, true, true)}
                        value={value}
                        isActive={
                            Array.isArray(selectedFilter)
                                ? selectedFilter[0] === value
                                : selectedFilter === value
                        }
                    >
                        <span>{display}</span>
                    </FilterButton>
                </div>
            )),
    };

    state = {
        // the currently selected filter in this section
        selectedFilter: this.props.useAppState
            ? this.props.activeFilterOption
            : Object.keys(this.props.filters)[0],
    };

    componentDidMount() {
        // when the page is loaded for the first time, we check the url to see whether
        // a filter should be set in the radio buttons
        this._parseUrl(this.props.location, false);
    }

    componentDidUpdate(prevProps) {
        // if the user clicks the back or forward button, we check the new url to see
        // if a filter needs to be applied
        if (this.props.location !== prevProps.location) {
            this._parseUrl(this.props.location, true);
        }
    }

    /**
     * Examine the url and apply any filters that are present in the url
     *
     * @param {string} url - the url of this page
     * @param {bool} shouldSearch - should we make an ajax search request to the server
     *
     */
    _parseUrl = (location, shouldSearch) => {
        const { urlParameterMapping } = this.props;
        const url = location.pathname;
        const query = location.search;

        if (!urlParameterMapping) {
            return;
        }

        if (urlParameterMapping.isInQuery && query) {
            //check if urlParameter in query
            const parsedQuery = parseQuery(query);

            for (const key in urlParameterMapping) {
                if (parsedQuery[key]) {
                    const val = parsedQuery[key].split(',');

                    if (val) {
                        this._handleOptionChange(val, shouldSearch, false);
                        return;
                    }
                }
            }
        } else {
            // Check if applied filters are present in URL
            const urlSplit = url.split('/');

            if (urlSplit.length > 3) {
                // individual filters are separated by double dashes
                const urlParams = urlSplit[3].split('--');

                // check every possible filter to see whether it exists in this url
                for (const key in urlParameterMapping) {
                    if (urlParams.includes(key)) {
                        this._handleOptionChange(
                            urlParameterMapping[key],
                            shouldSearch,
                            false,
                        );

                        // only one filter at a time so return when we find one
                        return;
                    }
                }

                // if we can't find any filters in the url, we apply the 'all' filter
                this._handleOptionChange('all', shouldSearch, false);
            }
        }
    };

    /**
     * Handle a change of the filter setting. This is triggered in three cases.
     *
     * 1. The initial page load. We want to set the radio value but not trigger a new search
     * because the server has already included the search results in the html.
     *
     * 2. The back or forward button is pressed. We want to set the radio button and trigger
     * a new search but we don't want to update the url path location.
     *
     * 3. The user has selected a new radio option.  We want to set the radio button, trigger
     * a new search, and update the url path to represent the new option state.
     *
     * @param {string} value - the new selected value to be set for the radio buttons
     * @param {bool} shouldSearch - should we make an ajax search request to the server
     * @param {bool} shouldUpdateRoute - should we set a new location in the url
     *
     */
    _handleOptionChange = (value, shouldSearch, shouldUpdateRoute) => {
        const { onOptionChange } = this.props;

        if (typeof value === 'string') {
            if (value !== this.state.selectedFilter) {
                this.setState({ selectedFilter: value });

                if (shouldSearch) {
                    onOptionChange(value, shouldUpdateRoute);
                }
            }
        } else {
            if (!isEqual(value.sort(), this.state.selectedFilter)) {
                this.setState({ selectedFilter: value });

                if (shouldSearch) {
                    onOptionChange(value, shouldUpdateRoute);
                }
            }
        }
    };

    render() {
        const { filters } = this.props;
        const { radioValues } = getRadioValues(filters);

        return (
            <div className="filter-section" data-testid="filter-section">
                {this.props.renderFilters(
                    radioValues,
                    this.state.selectedFilter,
                    this._handleOptionChange,
                    this.props.isMobile,
                )}
            </div>
        );
    }
}

// withRouter adds a location field to props which allows us to watch for url changes
export default withRouter(FilterSection);
